import { useForm } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, redirect, stripSearchParams } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import { z } from "zod";

import type {
	MfaRequiredResponse,
	NewPasswordRequiredResponse,
	Session,
	SessionOpenedResponse,
	SignInResponse,
} from "@ravnar/api-contract";

import { completeNewPassword, sessionQueryOptions, signIn, startSession } from "../api/session";
import { ConfirmEmailForm, type SignUpCredentials } from "../components/confirm-email-form";
import { MfaCodeForm } from "../components/mfa-code-form";
import { MfaSetupStep } from "../components/mfa-setup-step";
import { PageHeader } from "../components/shell/app-shell";
import { PublicPage } from "../components/shell/public-page";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Field, Input } from "../components/ui/field";
import { DEMO_ACCOUNT } from "../demo-data/organization";
import { fieldErrorMessage } from "../lib/form";

// `redirect` is the page to open after signing in. Only same-origin paths such as "/work?item=W-2481" pass: anything
// else ("https://elsewhere.example", "//elsewhere.example") falls back to home, so a crafted link cannot send someone
// to another site.
const loginSearchSchema = z.object({
	redirect: z
		.string()
		.regex(/^\/(?![/\\])/)
		.default("/")
		.catch("/"),
});

const signInFormSchema = z.object({
	email: z.email("Enter a valid email address"),
	password: z.string().min(1, "Password is required"),
});

const newPasswordFormSchema = z
	.object({
		newPassword: z.string().min(12, "Use at least 12 characters").max(128, "Use at most 128 characters"),
		confirmPassword: z.string(),
	})
	.refine(value => value.confirmPassword === value.newPassword, {
		message: "The passwords do not match",
		path: ["confirmPassword"],
	});

export const Route = createFileRoute("/login")({
	validateSearch: loginSearchSchema,
	search: { middlewares: [stripSearchParams({ redirect: "/" })] },
	// Someone who is already signed in has nothing to do here.
	beforeLoad: async ({ context, search }) => {
		const session = await context.queryClient.query(sessionQueryOptions());
		if (session !== null) {
			throw redirect({ href: search.redirect });
		}
	},
	head: () => ({ meta: [{ title: "Sign in · Ravnar" }] }),
	staticData: { public: true },
	component: LoginPage,
});

function LoginPage() {
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const queryClient = useQueryClient();
	// Set once the API asks a member who signed in with a temporary password to choose their own.
	const [challenge, setChallenge] = useState<NewPasswordRequiredResponse | undefined>();
	// Set once the API asks someone who created an organization but has not confirmed it for the code it emailed.
	const [signUp, setSignUp] = useState<SignUpCredentials | undefined>();
	// Set once the API asks a member with an authenticator app for its code.
	const [mfaChallenge, setMfaChallenge] = useState<MfaRequiredResponse | undefined>();
	// Set once the API asks a member whose organization requires a factor, and who has none, to set one up.
	const [mfaSetup, setMfaSetup] = useState<SignUpCredentials | undefined>();

	const continueSignedIn = async (session: Session) => {
		startSession(queryClient, session);
		await navigate({ href: search.redirect, replace: true });
	};

	let step: ReactNode;
	if (signUp !== undefined) {
		step = (
			<ConfirmEmailForm
				email={signUp.email}
				password={signUp.password}
				onSignedIn={continueSignedIn}
				onStartAgain={() => {
					setSignUp(undefined);
				}}
			/>
		);
	} else if (mfaChallenge !== undefined) {
		step = (
			<MfaCodeForm
				challenge={mfaChallenge}
				onSignedIn={continueSignedIn}
				onStartAgain={() => {
					setMfaChallenge(undefined);
				}}
			/>
		);
	} else if (mfaSetup !== undefined) {
		step = (
			<MfaSetupStep
				email={mfaSetup.email}
				password={mfaSetup.password}
				onSignedIn={continueSignedIn}
				onStartAgain={() => {
					setMfaSetup(undefined);
				}}
			/>
		);
	} else if (challenge === undefined) {
		step = (
			<SignInForm
				onSignedIn={continueSignedIn}
				onNewPasswordRequired={setChallenge}
				onEmailConfirmationRequired={setSignUp}
				onMfaRequired={setMfaChallenge}
				onMfaSetupRequired={setMfaSetup}
			/>
		);
	} else {
		step = (
			<NewPasswordForm
				challenge={challenge}
				onSignedIn={continueSignedIn}
				onMfaSetupRequired={setMfaSetup}
				onStartAgain={() => {
					setChallenge(undefined);
				}}
			/>
		);
	}

	return (
		<PublicPage>
			<Card className="p-6">{step}</Card>
			{/* The demo account only exists in the local API that development runs against. */}
			{import.meta.env.DEV ? (
				<p className="mt-3 text-center text-caption text-ink-3">
					Demo account: {DEMO_ACCOUNT.email} · password {DEMO_ACCOUNT.password}
				</p>
			) : null}
		</PublicPage>
	);
}

interface SignInFormProps {
	readonly onSignedIn: (session: Session) => Promise<void>;
	readonly onNewPasswordRequired: (challenge: NewPasswordRequiredResponse) => void;
	readonly onEmailConfirmationRequired: (signUp: SignUpCredentials) => void;
	readonly onMfaRequired: (challenge: MfaRequiredResponse) => void;
	readonly onMfaSetupRequired: (credentials: SignUpCredentials) => void;
}

function SignInForm({
	onSignedIn,
	onNewPasswordRequired,
	onEmailConfirmationRequired,
	onMfaRequired,
	onMfaSetupRequired,
}: SignInFormProps) {
	const mutation = useMutation({ mutationFn: signIn });

	const form = useForm({
		defaultValues: { email: "", password: "" },
		// Zod schemas are Standard Schema validators, so no adapter is needed.
		validators: { onChange: signInFormSchema, onSubmit: signInFormSchema },
		onSubmit: async ({ value }) => {
			let response: SignInResponse;
			try {
				response = await mutation.mutateAsync(value);
			} catch {
				// The alert below shows mutation.error. Rethrowing would make handleSubmit reject unhandled.
				return;
			}
			if (response.status === "new-password-required") {
				onNewPasswordRequired(response);
				return;
			}
			if (response.status === "email-confirmation-required") {
				onEmailConfirmationRequired({ email: response.email, password: value.password });
				return;
			}
			if (response.status === "mfa-required") {
				onMfaRequired(response);
				return;
			}
			if (response.status === "mfa-setup-required") {
				onMfaSetupRequired({ email: response.email, password: value.password });
				return;
			}
			await onSignedIn(response.session);
		},
	});

	return (
		<>
			<PageHeader title="Sign in" description="Use your work email and password." />
			<form
				noValidate
				className="flex flex-col gap-4"
				onSubmit={event => {
					event.preventDefault();
					event.stopPropagation();
					void form.handleSubmit();
				}}
			>
				<form.Field name="email">
					{field => (
						<Field label="Email" error={fieldErrorMessage(field.state.meta)}>
							<Input
								type="email"
								name={field.name}
								autoComplete="username"
								value={field.state.value}
								onBlur={field.handleBlur}
								onChange={event => {
									field.handleChange(event.target.value);
								}}
							/>
						</Field>
					)}
				</form.Field>
				<form.Field name="password">
					{field => (
						<Field label="Password" error={fieldErrorMessage(field.state.meta)}>
							<Input
								type="password"
								name={field.name}
								autoComplete="current-password"
								value={field.state.value}
								onBlur={field.handleBlur}
								onChange={event => {
									field.handleChange(event.target.value);
								}}
							/>
						</Field>
					)}
				</form.Field>
				<p className="-mt-1 text-caption">
					<Link to="/forgot-password" className="font-medium text-accent hover:underline">
						Forgot your password?
					</Link>
				</p>
				{mutation.isError ? (
					<p role="alert" className="text-caption text-alert">
						{mutation.error.message}
					</p>
				) : null}
				<form.Subscribe selector={state => [state.canSubmit, state.isSubmitting]}>
					{([canSubmit, isSubmitting]) => (
						<Button
							type="submit"
							variant="primary"
							className="w-full justify-center"
							disabled={!canSubmit || isSubmitting}
						>
							{isSubmitting ? "Signing in…" : "Sign in"}
						</Button>
					)}
				</form.Subscribe>
			</form>
			<p className="mt-5 border-t border-line pt-4 text-center text-body text-ink-2">
				New to Ravnar?{" "}
				<Link to="/organizations/new" className="font-medium text-accent hover:underline">
					Create an organization
				</Link>
			</p>
		</>
	);
}

interface NewPasswordFormProps {
	readonly challenge: NewPasswordRequiredResponse;
	readonly onSignedIn: (session: Session) => Promise<void>;
	readonly onMfaSetupRequired: (credentials: SignUpCredentials) => void;
	readonly onStartAgain: () => void;
}

/** The second step for a member who was added to a team: their invitation's temporary password must be replaced. */
function NewPasswordForm({ challenge, onSignedIn, onMfaSetupRequired, onStartAgain }: NewPasswordFormProps) {
	const mutation = useMutation({ mutationFn: completeNewPassword });

	const form = useForm({
		defaultValues: { newPassword: "", confirmPassword: "" },
		// Zod schemas are Standard Schema validators, so no adapter is needed.
		validators: { onChange: newPasswordFormSchema, onSubmit: newPasswordFormSchema },
		onSubmit: async ({ value }) => {
			let response: SessionOpenedResponse;
			try {
				response = await mutation.mutateAsync({
					email: challenge.email,
					challengeSession: challenge.challengeSession,
					newPassword: value.newPassword,
				});
			} catch {
				// The alert below shows mutation.error. Rethrowing would make handleSubmit reject unhandled.
				return;
			}
			if (response.status === "mfa-setup-required") {
				onMfaSetupRequired({ email: response.email, password: value.newPassword });
				return;
			}
			await onSignedIn(response.session);
		},
	});

	return (
		<>
			<PageHeader
				title="Choose your password"
				description={`You signed in as ${challenge.email} with a temporary password. Choose your own to continue.`}
			/>
			<form
				noValidate
				className="flex flex-col gap-4"
				onSubmit={event => {
					event.preventDefault();
					event.stopPropagation();
					void form.handleSubmit();
				}}
			>
				<form.Field name="newPassword">
					{field => (
						<Field label="New password" error={fieldErrorMessage(field.state.meta)}>
							<Input
								type="password"
								name={field.name}
								autoComplete="new-password"
								value={field.state.value}
								onBlur={field.handleBlur}
								onChange={event => {
									field.handleChange(event.target.value);
								}}
							/>
						</Field>
					)}
				</form.Field>
				<form.Field name="confirmPassword">
					{field => (
						<Field label="Confirm new password" error={fieldErrorMessage(field.state.meta)}>
							<Input
								type="password"
								name={field.name}
								autoComplete="new-password"
								value={field.state.value}
								onBlur={field.handleBlur}
								onChange={event => {
									field.handleChange(event.target.value);
								}}
							/>
						</Field>
					)}
				</form.Field>
				<p className="text-caption text-ink-3">Use at least 12 characters.</p>
				{mutation.isError ? (
					<p role="alert" className="text-caption text-alert">
						{mutation.error.message}
					</p>
				) : null}
				<form.Subscribe selector={state => [state.canSubmit, state.isSubmitting]}>
					{([canSubmit, isSubmitting]) => (
						<Button
							type="submit"
							variant="primary"
							className="w-full justify-center"
							disabled={!canSubmit || isSubmitting}
						>
							{isSubmitting ? "Signing in…" : "Set password and sign in"}
						</Button>
					)}
				</form.Subscribe>
			</form>
			<p className="mt-5 border-t border-line pt-4 text-center text-body text-ink-2">
				Not you, or took too long?{" "}
				<button
					type="button"
					className="cursor-pointer font-medium text-accent hover:underline"
					onClick={onStartAgain}
				>
					Sign in again
				</button>
			</p>
		</>
	);
}
